import { getLocalName } from 'vet-bones/utils';

import { RelationshipDirection } from 'src/containers/ProjectDiagram/constants/modelConcept';
import {
  MappingIdentifierPart,
  MappingIdentifierType,
} from 'src/containers/ProjectDiagram/types/data';
import { ModelSnapshot } from 'src/containers/ProjectDiagram/types/model';
import {
  isModelConceptClass,
  isModelConceptRelationship,
} from 'src/containers/ProjectDiagram/utils/data';
import { getModelConceptClassValue } from 'src/containers/ProjectDiagram/utils/modelConceptClass/createModelConceptClass';
import { SuggestionType } from 'src/containers/ProjectMapping/types';
import { SuggestionWithoutResource } from 'src/utils/jsonld/parseSuggestionResults';
import { GenesisMapping } from 'src/utils/voicebox/hooks/types';

/**
 * Transforms Genesis mappings to suggestions using the existing diagram model.
 * This is used for workflow 2b where we have an existing model in the diagram
 * and don't need to extract JSON-LD.
 */
export const transformGenesisMappingsFromDiagram = (
  mappings: GenesisMapping[],
  modelSnapshot: ModelSnapshot
): SuggestionWithoutResource[] => {
  if (!mappings.length) {
    return [];
  }
  const suggestions: SuggestionWithoutResource[] = [];

  const classByIri = new Map<
    string,
    { key: string; iri: string; label: string }
  >();
  const attributesByClassIri = new Map<
    string,
    Array<{ iri: string; label: string; ranges: string[] }>
  >();
  const relationshipsByIri = new Map<
    string,
    {
      iri: string;
      label: string;
      fromKey: string;
      toKey: string;
    }
  >();

  modelSnapshot.nodeDataArray.forEach((node) => {
    if (isModelConceptClass(node)) {
      const classIri = node.value;
      classByIri.set(classIri, {
        key: node.key,
        iri: classIri,
        label: getLocalName(classIri),
      });
    }
  });

  const modelData = modelSnapshot.modelData || {};
  const labelsByIri = modelData.labelsByIri || {};
  const attributesByIri = modelData.attributesByIri || {};

  Object.entries(attributesByIri).forEach(([iri, attribute]) => {
    const domains = Object.keys(attribute.domains || {});
    const ranges = Object.keys(attribute.ranges || {});

    domains.forEach((domainIri) => {
      const existing = attributesByClassIri.get(domainIri) || [];
      existing.push({
        iri,
        label: labelsByIri[iri] || getLocalName(iri),
        ranges,
      });
      attributesByClassIri.set(domainIri, existing);
    });
  });

  modelSnapshot.linkDataArray?.forEach((link) => {
    if (isModelConceptRelationship(link) && link.from && link.to) {
      relationshipsByIri.set(link.value, {
        iri: link.value,
        label: getLocalName(link.value),
        fromKey: link.from,
        toKey: link.to,
      });
    }
  });

  mappings.forEach((mapping) => {
    const {
      table_id: tableId,
      concept: classIri,
      id: idFields,
      label: labelFields,
      columns: columnMappings,
    } = mapping;
    const source = tableId;

    const classInfo = classByIri.get(classIri);
    if (!classInfo) {
      return;
    }

    if (idFields.length) {
      const fields: MappingIdentifierPart[] = idFields.map((field) => ({
        type: MappingIdentifierType.FIELD,
        value: field,
      }));

      suggestions.push({
        type: SuggestionType.PRIMARY_IDENTIFIER,
        score: 1.0,
        fields,
        source,
      });
    }

    if (labelFields.length) {
      labelFields.forEach((labelField) => {
        suggestions.push({
          type: SuggestionType.LABEL,
          field: labelField,
          score: 1.0,
          source,
        });
      });
    }

    if (columnMappings) {
      const classAttributes = attributesByClassIri.get(classIri) || [];
      const notFound: string[] = [];

      Object.entries(columnMappings).forEach(([propertyIri, columns]) => {
        const columnArray = Array.isArray(columns) ? columns : [columns];

        const attribute = classAttributes.find(
          (attr) => attr.iri === propertyIri
        );
        if (attribute) {
          const fieldValue = columnArray[0];

          attribute.ranges.forEach((rangeIri) => {
            suggestions.push({
              type: SuggestionType.ATTRIBUTE,
              iri: attribute.iri,
              field: fieldValue,
              label: attribute.label,
              propertyType: rangeIri,
              classIri,
              classLabel: classInfo.label,
              score: 1.0,
              source,
            });
          });
          return;
        }

        const relationship = relationshipsByIri.get(propertyIri);
        if (relationship) {
          const fields: MappingIdentifierPart[] = columnArray.map((column) => ({
            type: MappingIdentifierType.FIELD,
            value: column,
          }));

          const currentClassKey = classInfo.key;
          const isOutgoing = relationship.fromKey === currentClassKey;
          const isIncoming = relationship.toKey === currentClassKey;

          if (isOutgoing) {
            const toIri = getModelConceptClassValue(relationship.toKey);
            const rangeClass = classByIri.get(toIri);
            if (rangeClass) {
              suggestions.push({
                type: SuggestionType.RELATIONSHIP,
                iri: relationship.iri,
                fields,
                label: relationship.label,
                propertyType: rangeClass.iri,
                propertyTypeLabel: rangeClass.label,
                classIri,
                classLabel: classInfo.label,
                direction: RelationshipDirection.OUTGOING,
                score: 1.0,
                source,
              });
            }
          }

          if (isIncoming) {
            const fromIri = getModelConceptClassValue(relationship.fromKey);
            const domainClass = classByIri.get(fromIri);
            if (domainClass) {
              suggestions.push({
                type: SuggestionType.RELATIONSHIP,
                iri: relationship.iri,
                fields,
                label: relationship.label,
                propertyType: domainClass.iri,
                propertyTypeLabel: domainClass.label,
                classIri,
                classLabel: classInfo.label,
                direction: RelationshipDirection.INCOMING,
                score: 1.0,
                source,
              });
            }
          }
        } else {
          notFound.push(propertyIri);
        }
      });
    }

    suggestions.push({
      type: SuggestionType.CLASS,
      iri: classIri,
      label: classInfo.label,
      score: 1.0,
      source,
    });
  });

  return suggestions;
};
